import lodashDebounce from 'lodash/debounce';

/**
 * Creates a debounced version of a function that delays execution until after
 * the timeout elapses since the last call. Supports sync and async functions.
 *
 * All callers within the debounce window receive the result of the **last call's args**.
 *
 * **Race-condition safety:** if two separate debounce windows both fire (i.e. two
 * requests are simultaneously in-flight), the result of the *earlier* request is
 * silently discarded so it can never resolve callers that already belong to the
 * newer window. This is handled internally — the `fn` signature is unchanged and
 * all existing call-sites continue to work without modification.
 *
 * @param fn - The function to debounce.
 * @param timeout - Delay in milliseconds. Default is 500ms.
 * @returns A debounced function returning a Promise. Exposes `cancel()` and `flush()`.
 * - `cancel()` — cancels any pending invocation, aborts any in-flight request,
 *                and rejects all waiting callers.
 * - `flush()` — immediately invokes the pending call if one exists; resolves to
 *               `undefined` if nothing is pending.
 *
 * @example
 * const debouncedSearch = debounce(async (query: string) => {
 *   const results = await UserApi.paginated(query);
 *   return results;
 * }, 300);
 *
 * debouncedSearch('ah-').then((response) => console.log(response));
 * debouncedSearch('ahmed').then((response) => console.log(response));
 * // ^ If 'ah-' resolves after 'ahmed', its stale result is automatically dropped.
 */

export type DebouncedAsyncFunction<TArgs extends any[], TReturn> = ((...args: TArgs) => Promise<Awaited<TReturn>>) & {
  cancel: () => void;
  flush: () => Promise<Awaited<TReturn> | undefined>;
};

export type DebounceCanceledError = Error & {
  code: 'DEBOUNCE_CANCELED';
};

const createDebounceCanceledError = (): DebounceCanceledError => {
  const error = new Error('Debounced call canceled') as DebounceCanceledError;
  error.name = 'DebounceCanceledError';
  error.code = 'DEBOUNCE_CANCELED';
  return error;
};

export const debounce = <TArgs extends any[], TReturn>(
  fn: (...args: TArgs) => TReturn | Promise<TReturn>,
  timeout: number = 500,
): DebouncedAsyncFunction<TArgs, TReturn> => {
  type Value = Awaited<TReturn>;

  const pending: Array<{
    resolve: (value: Value) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  let abortController: AbortController | null = null;

  const resolvePending = (value: Value): void => {
    pending.splice(0).forEach(({ resolve }) => resolve(value));
  };

  const rejectPending = (reason: unknown): void => {
    pending.splice(0).forEach(({ reject }) => reject(reason));
  };

  const debouncedRunner = lodashDebounce((...args: TArgs): void => {
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    const { signal } = abortController;

    Promise.resolve(fn(...args))
      .then((value) => {
        if (signal.aborted) {
          return;
        }
        resolvePending(value as Value);
      })
      .catch((error) => {
        if (signal.aborted) {
          return;
        }
        rejectPending(error);
      });
  }, timeout);

  const wrapped = ((...args: TArgs): Promise<Value> => {
    return new Promise<Value>((resolve, reject) => {
      pending.push({ resolve, reject });
      debouncedRunner(...args);
    });
  }) as DebouncedAsyncFunction<TArgs, TReturn>;

  wrapped.cancel = (): void => {
    debouncedRunner.cancel();
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    rejectPending(createDebounceCanceledError());
  };

  wrapped.flush = (): Promise<Value | undefined> => {
    if (pending.length === 0) {
      return Promise.resolve(undefined);
    }
    const flushPromise = new Promise<Value>((resolve, reject) => {
      pending.push({ resolve, reject });
    });
    debouncedRunner.flush();
    return flushPromise;
  };

  return wrapped;
};
